import { Box, Button, Card, Divider, Group, Image, Select, Stack, Tabs, Text } from '@mantine/core';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import PaletteColourField from 'src/secret-menu/brand-asset-builder/components/PaletteColourField';
import PreviewCanvas from 'src/secret-menu/brand-asset-builder/components/PreviewCanvas';
import { useGoogleFont } from 'src/secret-menu/brand-asset-builder/hooks/useGoogleFont';
import { usePaletteColourDrafts } from 'src/secret-menu/brand-asset-builder/hooks/usePaletteColourDrafts';
import {
  DEFAULT_BRAND_ASSET_BUILDER_PALETTE,
  type BrandAssetBuilderData,
  type BrandAssetBuilderPalette,
} from 'src/secret-menu/brand-asset-builder/types';

const HEADING_FONT_OPTIONS = [
  'Aptos Display',
  'Arial',
  'Calibri',
  'Helvetica',
  'Times New Roman',
  'Poppins',
  'Montserrat',
  'Roboto',
  'Open Sans',
  'Lato',
];

const BODY_FONT_OPTIONS = [
  'Aptos',
  'Arial',
  'Calibri',
  'Helvetica',
  'Times New Roman',
  'Poppins',
  'Montserrat',
  'Roboto',
  'Open Sans',
  'Lato',
];

const PALETTE_FIELDS: {
  key: keyof BrandAssetBuilderPalette;
  label: string;
  description: string;
}[] = [
  { key: 'primary', label: 'Primary', description: 'Headings and title-page shapes' },
  { key: 'secondary', label: 'Secondary', description: 'Supporting shapes and dividers' },
  { key: 'accent', label: 'Accent', description: 'Highlights and callouts' },
  { key: 'darkText', label: 'Dark text', description: 'Body copy and dark-on-light text' },
  { key: 'background', label: 'Background', description: 'Page background' },
];

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function PalettePreviewDocument({ data }: { data: BrandAssetBuilderData }) {
  const { palette, headingFont, bodyFont, logoUrl, clientName } = data;

  return (
    <Card
      withBorder
      radius="md"
      p="xl"
      style={{ width: 420, height: 594, backgroundColor: '#ffffff', overflow: 'hidden' }}
    >
      <Stack gap="lg">
        <Group justify="space-between" align="center" wrap="nowrap">
          {logoUrl ? (
            <Image src={logoUrl} alt="Tenant logo" fit="contain" mah={28} maw={100} />
          ) : (
            <Box
              w={90}
              h={28}
              bg="gray.1"
              style={{
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text size="xxs" c="gray.5">
                Company logo
              </Text>
            </Box>
          )}
          <Stack gap={0} align="flex-end">
            <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.7">
              {clientName || 'Client name'}
            </Text>
            <Text size="xxs" c="gray.5">
              Export assets
            </Text>
          </Stack>
        </Group>

        <Divider color={palette.primary} size="sm" />

        <Text size="lg" fw={FONT_WEIGHTS.bold} c={palette.primary} ff={headingFont}>
          Strategic overview
        </Text>

        <Text size="xs" c={palette.darkText} ff={bodyFont} lh={1.6}>
          This section summarises performance across the review period, highlighting the key
          movements in valuation, contributions and overall progress against objectives.
        </Text>

        <Text size="sm" fw={FONT_WEIGHTS.semibold} c={palette.primary} ff={headingFont}>
          1. Key figures
        </Text>

        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: bodyFont,
          }}
        >
          <thead>
            <tr style={{ backgroundColor: hexToRgba(palette.primary, 0.18) }}>
              <th
                style={{
                  border: `1px solid ${palette.secondary}`,
                  padding: '6px 8px',
                  textAlign: 'left',
                  fontSize: 11,
                  color: palette.darkText,
                }}
              >
                Metric
              </th>
              <th
                style={{
                  border: `1px solid ${palette.secondary}`,
                  padding: '6px 8px',
                  textAlign: 'left',
                  fontSize: 11,
                  color: palette.darkText,
                }}
              >
                Target
              </th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Revenue', '15% YoY'],
              ['Retention', '98%'],
            ].map(([metric, target]) => (
              <tr key={metric}>
                <td
                  style={{
                    border: `1px solid ${palette.secondary}`,
                    padding: '6px 8px',
                    fontSize: 11,
                    color: palette.darkText,
                  }}
                >
                  {metric}
                </td>
                <td
                  style={{
                    border: `1px solid ${palette.secondary}`,
                    padding: '6px 8px',
                    fontSize: 11,
                    color: palette.darkText,
                  }}
                >
                  {target}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <Box
          style={{
            borderLeft: `3px solid ${palette.accent}`,
            paddingLeft: 'var(--mantine-spacing-sm)',
          }}
        >
          <Text size="xs" fs="italic" c={palette.darkText} ff={bodyFont}>
            &ldquo;A clear, consistent presentation of client outcomes across every reporting
            cycle.&rdquo;
          </Text>
        </Box>
      </Stack>
    </Card>
  );
}

export default function ReviewPaletteStep(props: StepProps<BrandAssetBuilderData>) {
  const { data, setData } = props;

  useGoogleFont(data.headingFont);
  useGoogleFont(data.bodyFont);

  const {
    drafts: paletteDrafts,
    draftColour: draftPaletteColour,
    handleFocus: handleFieldFocus,
    handleBlur: handleFieldBlur,
  } = usePaletteColourDrafts(data.palette, (key, value) =>
    setData({ ...data, palette: { ...data.palette, [key]: value } }),
  );

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Review palette & fonts"
      description="These placeholder colours will be replaced with ones extracted from the logo automatically in a later update. Adjust the palette and fonts below - the preview on the left updates live."
      showClientName={false}
      hideProgress
      maxContainerWidth="lg"
    >
      <Group
        align="flex-start"
        gap="xl"
        wrap="nowrap"
        h="100%"
        style={{ alignItems: 'flex-start' }}
      >
        <Box style={{ flexShrink: 0, width: 420, height: '100%' }}>
          <PreviewCanvas nativeWidth={420} nativeHeight={594}>
            <PalettePreviewDocument data={data} />
          </PreviewCanvas>
        </Box>

        <Box flex={1} miw={0} h="100%" style={{ overflow: 'hidden' }}>
          <Tabs
            defaultValue="colours"
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <Tabs.List>
              <Tabs.Tab value="colours">Colours</Tabs.Tab>
              <Tabs.Tab value="typography">Typography</Tabs.Tab>
            </Tabs.List>

            <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Tabs.Panel value="colours" pt="md">
                <Stack gap="md">
                  <Group justify="flex-end">
                    <Button
                      variant="subtle"
                      color="gray"
                      size="xs"
                      onClick={() =>
                        setData({ ...data, palette: DEFAULT_BRAND_ASSET_BUILDER_PALETTE })
                      }
                    >
                      Reset to placeholder defaults
                    </Button>
                  </Group>
                  <Stack gap="sm">
                    {PALETTE_FIELDS.map(field => (
                      <PaletteColourField
                        key={field.key}
                        label={field.label}
                        description={field.description}
                        value={paletteDrafts[field.key]}
                        onChange={value => draftPaletteColour(field.key, value)}
                        onFocus={() => handleFieldFocus(field.key)}
                        onBlur={() => handleFieldBlur(field.key)}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="typography" pt="md">
                <Stack gap="md">
                  <Select
                    label="Heading font"
                    data={HEADING_FONT_OPTIONS}
                    value={data.headingFont}
                    onChange={value => setData({ ...data, headingFont: value ?? data.headingFont })}
                    allowDeselect={false}
                  />
                  <Select
                    label="Body font"
                    data={BODY_FONT_OPTIONS}
                    value={data.bodyFont}
                    onChange={value => setData({ ...data, bodyFont: value ?? data.bodyFont })}
                    allowDeselect={false}
                  />
                </Stack>
              </Tabs.Panel>
            </Box>
          </Tabs>
        </Box>
      </Group>
    </DrawerFlowScreenLayout>
  );
}
